import { useState } from 'react';
import {
  EmptyState,
  PrimaryButton,
  SecondaryButton,
  TextButton,
  StatusPill,
} from '@/lib';
import { Gated } from '@/app/CapabilitiesProvider';
import type { ApiClients } from '@/api/client';
import { useOrgUsers, type OrgUser } from '@/screens/S13Users/queries';
import {
  useGroups,
  useCreateGroup,
  useDeleteGroup,
  useAddGroupMember,
  useRemoveGroupMember,
  type Group,
} from './queries';

export type S14GroupsProps = {
  clients: ApiClients;
};

/**
 * S14 Groups. Composes R3 endpoints:
 *   - GET /v1/groups, POST /v1/groups, PATCH /v1/groups/{id}, DELETE
 *   - POST /v1/groups/{id}/members, DELETE .../{userId}
 *
 * All mutation affordances are wrapped in <Gated capability="group.manage">.
 * The scope field is freeform DSL — server validates on create/patch.
 */
export function S14Groups({ clients }: S14GroupsProps) {
  const groups = useGroups(clients);
  const users = useOrgUsers(clients);
  const [createOpen, setCreateOpen] = useState(false);

  if (groups.isError) {
    return (
      <EmptyState
        variant="error"
        title="Couldn't load groups"
        body={String(groups.error)}
        cta={{ label: 'Retry', onClick: () => groups.refetch() }}
      />
    );
  }

  return (
    <div className="flex flex-col gap-sp-6">
      <header className="flex items-baseline justify-between">
        <div>
          <p className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700">
            Access control
          </p>
          <h1 className="mt-sp-1 text-fs-h1 font-medium text-gray-900">Groups</h1>
          <p className="mt-sp-1 text-fs-meta text-gray-700">
            {groups.data?.length ?? '—'} groups
          </p>
        </div>
        <Gated capability="group.manage">
          <PrimaryButton
            label={createOpen ? 'Cancel' : 'New group'}
            onClick={() => setCreateOpen((v) => !v)}
          />
        </Gated>
      </header>

      <Gated capability="group.manage">
        {createOpen ? (
          <CreateGroupForm clients={clients} onDone={() => setCreateOpen(false)} />
        ) : null}
      </Gated>

      <section aria-labelledby="groups-heading">
        <h2
          id="groups-heading"
          className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700"
        >
          All groups
        </h2>
        {groups.isLoading ? (
          <p className="mt-sp-3 text-fs-body text-gray-700">Loading groups…</p>
        ) : !groups.data || groups.data.length === 0 ? (
          <p className="mt-sp-3 text-fs-body text-gray-700">
            No groups yet. Create one to scope access by site, asset class, or sensor.
          </p>
        ) : (
          <ul className="mt-sp-3 flex flex-col gap-sp-3">
            {groups.data.map((g) => (
              <GroupRow key={g.id} group={g} clients={clients} users={users.data ?? []} />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function GroupRow({
  group,
  clients,
  users,
}: {
  group: Group;
  clients: ApiClients;
  users: OrgUser[];
}) {
  const [expanded, setExpanded] = useState(false);
  const remove = useDeleteGroup(clients);
  const addMember = useAddGroupMember(clients);
  const removeMember = useRemoveGroupMember(clients);
  const [newMember, setNewMember] = useState('');

  const memberLookup = new Map(users.map((u) => [u.user_id, u]));

  return (
    <li className="rounded-r-default border border-gray-300 bg-gray-50">
      <div className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-sp-3 px-sp-3 py-sp-2">
        <div className="min-w-0">
          <p className="truncate text-fs-body font-medium text-gray-900">{group.name}</p>
          <p className="truncate text-fs-meta text-gray-700">
            <span className="font-mono">{group.scope || '(no scope — matches everything)'}</span>
          </p>
        </div>
        <StatusPill level="watch" label={`${group.member_count} MEMBERS`} />
        <TextButton
          label={expanded ? 'Hide' : 'Members'}
          onClick={() => setExpanded((v) => !v)}
        />
        <Gated capability="group.manage">
          <TextButton
            label={remove.isPending ? '…' : 'Delete'}
            onClick={() => {
              if (window.confirm(`Delete group "${group.name}"?`)) {
                remove.mutate({ groupId: group.id });
              }
            }}
          />
        </Gated>
      </div>
      {expanded ? (
        <div className="border-t border-gray-300 px-sp-3 py-sp-2">
          {group.members.length === 0 ? (
            <p className="text-fs-meta text-gray-700">No members yet.</p>
          ) : (
            <ul className="flex flex-col gap-sp-1">
              {group.members.map((memberId) => {
                const u = memberLookup.get(memberId);
                return (
                  <li
                    key={memberId}
                    className="flex items-center justify-between text-fs-meta text-gray-900"
                  >
                    <span>{u ? `${u.display_name || u.email}` : memberId}</span>
                    <Gated capability="group.manage">
                      <TextButton
                        label={removeMember.isPending ? '…' : 'Remove'}
                        onClick={() =>
                          removeMember.mutate({ groupId: group.id, userId: memberId })
                        }
                      />
                    </Gated>
                  </li>
                );
              })}
            </ul>
          )}
          <Gated capability="group.manage">
            <form
              className="mt-sp-3 flex items-center gap-sp-2"
              onSubmit={(e) => {
                e.preventDefault();
                if (!newMember) return;
                addMember.mutate(
                  { groupId: group.id, userId: newMember },
                  { onSuccess: () => setNewMember('') },
                );
              }}
            >
              <label className="flex-1 text-fs-meta text-gray-700">
                Add member
                <select
                  value={newMember}
                  onChange={(e) => setNewMember(e.target.value)}
                  className="mt-sp-1 w-full rounded-r-default border border-gray-300 bg-gray-50 px-sp-2 py-sp-1 text-fs-body text-gray-900"
                >
                  <option value="">— select a user —</option>
                  {users
                    .filter((u) => !group.members.includes(u.user_id))
                    .map((u) => (
                      <option key={u.user_id} value={u.user_id}>
                        {u.display_name || u.email}
                      </option>
                    ))}
                </select>
              </label>
              <PrimaryButton
                label={addMember.isPending ? 'Adding…' : 'Add'}
                type="submit"
                disabled={!newMember || addMember.isPending}
              />
            </form>
          </Gated>
        </div>
      ) : null}
    </li>
  );
}

function CreateGroupForm({
  clients,
  onDone,
}: {
  clients: ApiClients;
  onDone: () => void;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [scope, setScope] = useState('');
  const create = useCreateGroup(clients);

  return (
    <form
      aria-labelledby="create-group-heading"
      className="rounded-r-card border border-gray-300 bg-gray-50 p-sp-6"
      onSubmit={(e) => {
        e.preventDefault();
        create.mutate(
          { name, description: description || undefined, scope: scope || undefined },
          { onSuccess: () => onDone() },
        );
      }}
    >
      <h2
        id="create-group-heading"
        className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700"
      >
        New group
      </h2>
      <div className="mt-sp-3 grid grid-cols-1 gap-sp-3">
        <label className="flex flex-col gap-sp-1 text-fs-body text-gray-900">
          Name
          <input
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="rounded-r-default border border-gray-300 bg-gray-50 px-sp-2 py-sp-1 text-fs-body text-gray-900"
          />
        </label>
        <label className="flex flex-col gap-sp-1 text-fs-body text-gray-900">
          Description
          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="rounded-r-default border border-gray-300 bg-gray-50 px-sp-2 py-sp-1 text-fs-body text-gray-900"
          />
        </label>
        <label className="flex flex-col gap-sp-1 text-fs-body text-gray-900">
          Scope
          <span className="text-fs-meta text-gray-700">
            DSL — e.g. <code className="font-mono">site = &quot;Plant A&quot; AND class = pump</code>.
            Leave blank to match everything.
          </span>
          <input
            value={scope}
            onChange={(e) => setScope(e.target.value)}
            placeholder='site = "Plant A"'
            className="rounded-r-default border border-gray-300 bg-gray-50 px-sp-2 py-sp-1 font-mono text-fs-body text-gray-900"
          />
        </label>
      </div>
      <div className="mt-sp-3 flex items-center gap-sp-3">
        <PrimaryButton
          label={create.isPending ? 'Creating…' : 'Create group'}
          type="submit"
          disabled={create.isPending || !name}
        />
        <SecondaryButton label="Cancel" type="button" onClick={onDone} />
        {create.isError ? (
          <span role="alert" className="text-fs-meta text-critical">
            {String(create.error)}
          </span>
        ) : null}
      </div>
    </form>
  );
}
